import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {catchError} from 'rxjs/operators';
import {Observable, throwError} from 'rxjs';
import {TaxplanIQServices} from "../services/TaxplanIQServices";
import {PATH, SOURCE_TYPE} from "../constants/Tpiq.constants";

export function getPaginatedToDosUrl(org, userId, channelType, channelId, status) {
  return `/TaxPlanningServices/orgs/${org}/users/${userId}/channels/${channelType}/${channelId}/toDos/list?status=${status}`;
}

export function getClientAppPaginatedToDosUrl(org, userId, status) {
  return `/TaxPlanningServices/users/${userId}/orgs/${org}/toDos/list?status=${status}`;
}

export function getToDosUrl(org, userId, channelType, channelId) {
  return `/TaxPlanningServices/orgs/${org}/users/${userId}/channels/${channelType}/${channelId}/toDos`;
}

export function getToDoWithPostUrl(userId, org, todoId, withPost) {
  return `/TaxPlanningServices/users/${userId}/orgs/${org}/toDos/${todoId}/?withPost=${withPost}`;
}

export function getFirmTodosTableDataUrl(org, userId) {
  return `/TaxPlanningServices/orgs/${org}/users/${userId}/toDos`;
}

export function getUpdateTodoStatusUrl(user, org, clientId, todoId) {
  return `/TaxPlanningServices/users/${user}/orgs/${org}/clients/${clientId}/toDos/${todoId}`;
}

export function getUpdateFirmTodoStatus(org, user) {
  return `/TaxPlanningServices/orgs/${org}/users/${user}/toDos/table-update`;
}

export function getRemoveToDosUrl(org, userId, clientId, todoId) {
  return `/TaxPlanningServices/orgs/${org}/users/${userId}/clients/${clientId}/toDos/${todoId}`;
}

export function getCreateTodoUrl(org, userId, taskId) {
  return `/TaxPlanningServices/orgs/${org}/users/${userId}/tasks/${taskId}/todo`;
}

export function getUpdateToDoDataUrl(org, userId, clientId, todoId) {
  return `/TaxPlanningServices/orgs/${org}/users/${userId}/clients/${clientId}/toDos/${todoId}`;
}

@Injectable({
  providedIn: "root"
})
export class TpFirmTodoService extends  TaxplanIQServices{
  constructor(http: HttpClient) {
    super(http);
  }

  getAllTodosTableData(org, userId, channelType, channelId, status, data, type?): Observable<any> {
    let url = '';
    if (type === 'all') {
      url = getClientAppPaginatedToDosUrl(org, userId, status);
    } else {
      url = getPaginatedToDosUrl(org, userId, channelType, channelId, status);
      if (channelType === 'companies') {
        url = `${url}&view=all`;
      }
    }
    return this.create(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  addToDo(org, userId, channelType, channelId, data): Observable<any> {
    const url = getToDosUrl(org, userId, channelType, channelId);
    return this.create(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  updateToDo(org, userId, channelType, channelId, data): Observable<any> {
    let url = getToDosUrl(org, userId, channelType, channelId);
    url = `${url}/${data.id}`;
    return this.update(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  removeToDo(org, userId, channelId, todoId): Observable<any> {
    let url = getRemoveToDosUrl(org, userId, channelId, todoId);
    return this.delete(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  getToDo(org, userId, channelType, channelId, todoId): Observable<any> {
    let url = getToDosUrl(org, userId, channelType, channelId);
    url = `${url}/${todoId}`;
    return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  getTodoTableData(user, org, todoId, withPost): Observable<any> {
    const url = getToDoWithPostUrl(user, org, todoId, withPost);
    return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  updateTodoStatusOrder(user, org, clientId, data): Observable<any> {
    const url = getUpdateTodoStatusUrl(user, org, clientId, data.id);
    return this.update(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  updateFirmTodoStatus(org, user, data): Observable<any> {
    const url = getUpdateFirmTodoStatus(org, user);
    return this.update(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  updateSinglePageTodo(org, user, taskId, data): Observable<any> {
    const url = this.interpolateUrl(PATH.ADD_SINGLEPAGE_TODO, null, {org, userId: user, taskId});
    return this.update(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  getFirmTodosTableData(org, userId, data): Observable<any> {
    const url = getFirmTodosTableDataUrl(org, userId);
    return this.create(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  getFirmTodosCount(org, userId, data): Observable<any> {
    let url = getFirmTodosTableDataUrl(org, userId);
    url = url + '/count';
    return this.create(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  updateFirmTodoData(org, userId, data): Observable<any> {
    let url = getFirmTodosTableDataUrl(org, userId);
    url = url + '/table-update';
    return this.update(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  addSinglePageTodo(org, user, taskId, data): Observable<any> {
    const url = getCreateTodoUrl(org, user, taskId);
    return this.create(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  updateTodoData(org, userId, clientId, data): Observable<any> {
    let url = getUpdateToDoDataUrl(org, userId, clientId, data.id);
    return this.update(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  private handleError(error: HttpErrorResponse): any {
    return throwError(error.error);
  }
}
